import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiDelete } from '../api';
import { formatNumberPrecise } from '../Utils';
import UserDetailModal from './UserDetailModal';
import { PERM, useCan } from '../permissions';

const updateUser = (payload) => apiPost('/api/admin/update-user', payload);
const updateBalance = (payload) => apiPost('/api/transaction/balance', payload);
const adjustGold = (payload) => apiPost('/api/admin/adjust-gold', payload);

const DarkModal = ({ show, onClose, children }) => {
    if (!show) return null;
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
            display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
        }}>
            <div style={{
                background: '#13132a',
                border: '1px solid rgba(255,77,109,0.2)',
                borderRadius: 18, padding: '28px 24px',
                width: '90%', maxWidth: 480,
                boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                position: 'relative', maxHeight: '90vh', overflowY: 'auto'
            }}>
                <button onClick={onClose} style={{
                    position: 'absolute', top: 14, right: 18,
                    background: 'none', border: 'none',
                    color: 'rgba(255,255,255,0.4)', fontSize: 20,
                    cursor: 'pointer', lineHeight: 1
                }}>×</button>
                {children}
            </div>
        </div>
    );
};

const AllUsers = () => {
    const can = useCan();
    // Diamonds are edited by typing over the current figure, so the field is
    // only offered to someone allowed to see it — the server strips Balance
    // without 'viewBalances', and a blank field would submit as a cut to zero.
    const canSeeBalance = can(PERM.VIEW_BALANCES);
    const canEditBalance = canSeeBalance && can(PERM.EDIT_BALANCE);
    // Gold is always visible in the table, so reducing it only needs the edit
    // grant. Server drops the whole request without it (off by default).
    const canEditGold = can(PERM.EDIT_GOLD);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [sortConfig, setSortConfig] = useState({ key: 'ID', direction: 'desc' });
    const [isLastPage, setIsLastPage] = useState(false);
    const [reload, setReload] = useState(0);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [userToEdit, setUserToEdit] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [editFormData, setEditFormData] = useState({ username: '', email: '', newPassword: '', Balance: 0, Gold: 0, agencyId: 0, role: 'user', moderation: false });
    const [clearingRoomId, setClearingRoomId] = useState(null);
    const [selectedUserForDetails, setSelectedUserForDetails] = useState(null);

    useEffect(() => {
        let alive = true;
        setLoading(true); setError(null);
        const params = new URLSearchParams({
            page: String(currentPage),
            limit: String(itemsPerPage),
            sortKey: sortConfig.key,
            sortDir: sortConfig.direction,
        });
        if (searchTerm.trim()) params.set('q', searchTerm.trim());
        apiGet(`/api/admin/users?${params.toString()}`)
            .then(data => {
                if (!alive) return;
                setUsers(data.users || []);
                setIsLastPage(!data.hasNext);
                setLoading(false);
            })
            .catch(() => { if (alive) { setError("Failed to load users."); setLoading(false); } });
        return () => { alive = false; };
    }, [currentPage, sortConfig, searchTerm, reload]);

    const handleSort = (key) => {
        setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
        setCurrentPage(1); setIsLastPage(false);
    };

    const handleSearch = () => {
        setSearchTerm(searchInput.trim());
        setCurrentPage(1);
    };

    const handleConfirmDelete = async () => {
        if (!userToDelete) return;
        setIsDeleting(true);
        try {
            const res = await apiDelete(`/api/admin/users/${userToDelete.id}`);
            if (res.success === false) throw new Error(res.message);
            alert(`User '${userToDelete.username}' removed.`);
            setReload(r => r + 1);
        } catch { alert("Failed to remove user."); }
        finally { setIsDeleting(false); setIsDeleteModalOpen(false); }
    };

    const handleConfirmUpdate = async (e) => {
        e.preventDefault();
        if (!userToEdit) return;
        // Checked before ANY request goes out: the balance write is the last of
        // up to two calls, so failing it afterwards would leave the profile edit
        // already applied and only report the failure.
        if (canEditBalance && Number(editFormData.Balance) > Number(userToEdit.Balance || 0)) {
            alert(`Balance can only be reduced here (current ${Number(userToEdit.Balance || 0)}). Use a coin send to add diamonds.`);
            return;
        }
        if (canEditGold && Number(editFormData.Gold) > Number(userToEdit.Gold || 0)) {
            alert(`Gold can only be reduced here (current ${Number(userToEdit.Gold || 0)}). Gold is earned, not granted.`);
            return;
        }
        setIsUpdating(true);
        try {
            const payload = {
                uid: userToEdit.uid,
                username: editFormData.username,
                email: editFormData.email,
                agencyId: Number(editFormData.agencyId),
                role: editFormData.role
            };
            // Needs the 'setModerator' permission, and the server rejects the
            // whole request if someone without it sends the field at all — so
            // send it only when it is actually editable AND actually changed.
            if (can(PERM.SET_MODERATOR) && editFormData.moderation !== (userToEdit.moderation === true)) {
                payload.moderation = editFormData.moderation;
            }
            if (editFormData.newPassword) {
                if (editFormData.newPassword.length < 6) throw new Error("Password must be at least 6 characters.");
                payload.newPassword = editFormData.newPassword;
            }
            const res = await updateUser(payload);
            if (res.success === false) throw new Error(res.message);
            if (canEditBalance && Number(editFormData.Balance) !== userToEdit.Balance) {
                const b = await updateBalance({ targetUid: userToEdit.id, targetCollection: 'users', newBalance: Number(editFormData.Balance) });
                if (b.success === false) throw new Error(b.message);
            }
            // Gold reduce is a separate atomic endpoint — it also lowers this
            // month's totalGold on the server (the User $inc mirror), so the
            // salary sheet drops to match.
            if (canEditGold && Number(editFormData.Gold) !== Number(userToEdit.Gold || 0)) {
                const g = await adjustGold({ targetUid: userToEdit.id, newGold: Number(editFormData.Gold) });
                if (g.success === false) throw new Error(g.message);
            }
            alert(`User '${userToEdit.username}' updated.`);
            setReload(r => r + 1);
        } catch (err) { alert(`Failed: ${err.message}`); }
        finally { setIsUpdating(false); setIsEditModalOpen(false); }
    };

    const handleClearRoom = async (user) => {
        if (!window.confirm(`Kick '${user.username}' from their room?`)) return;
        setClearingRoomId(user.id);
        try {
            const res = await updateUser({ uid: user.uid, currentRoom: null });
            if (res.success === false) throw new Error(res.message);
            alert(`Kicked '${user.username}'.`);
        }
        catch (err) { alert(`Failed: ${err.message}`); }
        finally { setClearingRoomId(null); }
    };

    const SortIcon = ({ colKey }) => {
        if (sortConfig.key !== colKey) return null;
        return <i className={`fas fa-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}`} style={{ marginLeft: 4, fontSize: 10 }} />;
    };

    if (loading && users.length === 0) return <div className="dark-loading"><div className="spinner" /><p>Loading All Users...</p></div>;
    if (error) return <div className="dark-error">{error}</div>;

    return (
        <div className="dark-page">
            <h2>Manage All Users</h2>

            <div className="search-row">
                <input
                    type="text"
                    className="dark-input"
                    style={{ maxWidth: 320 }}
                    placeholder="Search by User ID..."
                    value={searchInput}
                    onChange={e => { setSearchInput(e.target.value); if (!e.target.value) { setSearchTerm(''); setCurrentPage(1); } }}
                    onKeyDown={e => e.key === 'Enter' && handleSearch()}
                />
                <button
                    onClick={handleSearch}
                    style={{
                        background: 'linear-gradient(135deg,#ff4d6d,#ff2d55)', color: '#fff',
                        border: 'none', borderRadius: 8, padding: '9px 18px',
                        fontSize: 13, fontWeight: 600, cursor: 'pointer'
                    }}
                >
                    Search
                </button>
            </div>

            <div className="dark-table-wrap">
                <table className="dark-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('username')}>Name <SortIcon colKey="username" /></th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('ID')}>User ID <SortIcon colKey="ID" /></th>
                            <th>Email</th>
                            <th className="text-right" style={{ cursor: 'pointer' }} onClick={() => handleSort('Gold')}>Golds <SortIcon colKey="Gold" /></th>
                            {canSeeBalance && <th className="text-right" style={{ cursor: 'pointer' }} onClick={() => handleSort('Balance')}>Diamonds <SortIcon colKey="Balance" /></th>}
                            <th className="text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user, index) => (
                            <tr key={user.id}>
                                <td className="text-muted">{((currentPage - 1) * itemsPerPage) + index + 1}</td>
                                <td className="font-medium">
                                    <button className="dark-link" onClick={() => setSelectedUserForDetails(user.id)}>{user.username}</button>
                                </td>
                                <td>
                                    <button className="dark-link" onClick={() => setSelectedUserForDetails(user.id)}>{user.ID}</button>
                                </td>
                                <td className="text-muted">{user.email}</td>
                                <td className="text-right text-green">{formatNumberPrecise(user.Gold || 0)}</td>
                                {canSeeBalance && <td className="text-right text-green">{formatNumberPrecise(user.Balance || 0)}</td>}
                                <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                                    <button className="btn-action edit" style={{ marginRight: 4 }} onClick={() => { setUserToEdit(user); setEditFormData({ username: user.username, email: user.email, newPassword: '', Balance: user.Balance || 0, Gold: user.Gold || 0, agencyId: user.agencyId || 0, role: user.role || 'user', moderation: user.moderation === true }); setIsEditModalOpen(true); }} title="Edit">
                                        <i className="fas fa-edit" />
                                    </button>
                                    {can(PERM.DELETE_ACCOUNTS) && (
                                        <button className="btn-action delete" style={{ marginRight: 4 }} onClick={() => { setUserToDelete(user); setIsDeleteModalOpen(true); }} title="Delete">
                                            <i className="fas fa-trash-alt" />
                                        </button>
                                    )}
                                    <button className="btn-action warn" onClick={() => handleClearRoom(user)} disabled={clearingRoomId === user.id} title="Kick from Room">
                                        {clearingRoomId === user.id ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-sign-out-alt" />}
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {users.length === 0 && !loading && (
                            <tr className="empty-row"><td colSpan={canSeeBalance ? 7 : 6}>{searchTerm ? 'No user found with that ID.' : 'No users found.'}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {!searchTerm && (
                <div className="dark-pagination">
                    <button className="btn-page" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1 || loading}>Previous</button>
                    <span>Page {currentPage}</span>
                    <button className="btn-page" onClick={() => setCurrentPage(p => p + 1)} disabled={isLastPage || loading}>Next</button>
                </div>
            )}

            {/* Delete Modal */}
            <DarkModal show={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
                <div className="dark-confirm">
                    <div className="warn-icon"><i className="fas fa-exclamation-triangle" /></div>
                    <h3>Confirm Deletion</h3>
                    <p>
                        Permanently delete user <strong>{userToDelete?.username}</strong>?
                        This will remove their account and all transaction history.
                        <br /><strong style={{ color: '#fca5a5' }}>This action cannot be undone.</strong>
                    </p>
                    <div className="btn-row">
                        <button className="btn-page" onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}>Cancel</button>
                        <button className="btn-action delete" style={{ padding: '9px 20px', fontSize: 13 }} onClick={handleConfirmDelete} disabled={isDeleting}>
                            {isDeleting ? <><i className="fas fa-spinner fa-spin" /> Deleting...</> : 'Confirm Delete'}
                        </button>
                    </div>
                </div>
            </DarkModal>

            {/* Edit Modal */}
            <DarkModal show={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
                <h2 style={{ marginBottom: 16 }}>Edit User</h2>
                <form onSubmit={handleConfirmUpdate} className="dark-form">
                    <label>Username</label>
                    <input type="text" value={editFormData.username} onChange={e => setEditFormData(p => ({ ...p, username: e.target.value }))} required />
                    <label>Email</label>
                    <input type="email" value={editFormData.email} onChange={e => setEditFormData(p => ({ ...p, email: e.target.value }))} required />
                    <label>New Password</label>
                    <input type="password" value={editFormData.newPassword} onChange={e => setEditFormData(p => ({ ...p, newPassword: e.target.value }))} placeholder="Leave blank to keep current" />
                    {canEditBalance && <>
                        <label>Balance (Diamonds) — can only be reduced</label>
                        <input type="number" min="0" max={Number(userToEdit?.Balance || 0)} value={editFormData.Balance} onChange={e => setEditFormData(p => ({ ...p, Balance: e.target.value }))} />
                    </> }
                    {canEditGold && <>
                        <label>Gold — can only be reduced (also lowers this month's salary total)</label>
                        <input type="number" min="0" max={Number(userToEdit?.Gold || 0)} value={editFormData.Gold} onChange={e => setEditFormData(p => ({ ...p, Gold: e.target.value }))} />
                    </> }
                    <label>Agency ID</label>
                    <input type="number" value={editFormData.agencyId} onChange={e => setEditFormData(p => ({ ...p, agencyId: e.target.value }))} required />
                    <label>Role</label>
                    <select value={editFormData.role} onChange={e => setEditFormData(p => ({ ...p, role: e.target.value }))}>
                        <option value="user">User</option>
                        <option value="host">Host</option>
                    </select>
                    {can(PERM.SET_MODERATOR) && <>
                        <label>Moderator</label>
                        <select value={editFormData.moderation ? 'yes' : 'no'} onChange={e => setEditFormData(p => ({ ...p, moderation: e.target.value === 'yes' }))}>
                            <option value="no">No</option>
                            <option value="yes">Yes — can end any live</option>
                        </select>
                    </> }
                    <div className="btn-row">
                        <button type="button" className="btn-page" onClick={() => setIsEditModalOpen(false)} disabled={isUpdating}>Cancel</button>
                        <button type="submit" className="btn-action edit" style={{ padding: '9px 20px', fontSize: 13 }} disabled={isUpdating}>
                            {isUpdating ? 'Updating...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </DarkModal>

            {selectedUserForDetails && (
                <UserDetailModal userId={selectedUserForDetails} onClose={() => setSelectedUserForDetails(null)} />
            )}
        </div>
    );
};

export default AllUsers;
